// Pure helpers for the Action page: ordering, filtering, the counts its summary
// cards draw, and the words the page prints for each enum. No React here, so
// the rules are easy to test.
import type {
  ActionEffort,
  ActionItem,
  ActionStatus,
  Insight,
  Verdict,
} from "@dashboard-intelligent/api/src/modules/industry/types";
import { formatMetric } from "../../format";

export const EFFORT_TEXT: Record<ActionEffort, string> = {
  low: "Rendah",
  medium: "Sedang",
  high: "Tinggi",
};

export const STATUS_TEXT: Record<ActionStatus, string> = {
  open: "Belum mulai",
  doing: "Sedang dikerjakan",
  done: "Selesai",
};

export const STATUS_ORDER: ActionStatus[] = ["open", "doing", "done"];

const VERDICT_ORDER: Verdict[] = ["attention", "watch", "good"];

/** 1 is most urgent; ties keep the order the API sent. */
export function byPriority(actions: ActionItem[]): ActionItem[] {
  return actions
    .map((action, index) => ({ action, index }))
    .sort((a, b) => a.action.priority - b.action.priority || a.index - b.index)
    .map(({ action }) => action);
}

/** Distinct owners, in the order they first appear in the plan. */
export function ownersOf(actions: ActionItem[]): string[] {
  return [...new Set(actions.map((action) => action.owner))];
}

/** The verdicts the plan's insights actually carry, worst first. */
export function verdictsOf(actions: ActionItem[], insights: Map<string, Insight>): Verdict[] {
  const present = new Set(actions.map((action) => insights.get(action.insightId)?.verdict).filter(Boolean));
  return VERDICT_ORDER.filter((verdict) => present.has(verdict));
}

/** Empty filter lists mean "no filter". An action whose insight is missing only shows with no verdict filter. */
export function filterPlan(
  actions: ActionItem[],
  insights: Map<string, Insight>,
  owners: string[],
  verdicts: Verdict[],
): ActionItem[] {
  return actions.filter((action) => {
    if (owners.length > 0 && !owners.includes(action.owner)) return false;
    if (verdicts.length === 0) return true;
    const verdict = insights.get(action.insightId)?.verdict;
    return verdict !== undefined && verdicts.includes(verdict);
  });
}

/** "68 pendaftar", or null when the API says the reach is not countable. */
export function reachText(reach: ActionItem["reach"]): string | null {
  if (!reach) return null;
  return `${formatMetric(reach.value, "count")} ${reach.unit}`;
}

/** How many actions sit in each status, after the reader's local changes. */
export function countByStatus(actions: ActionItem[], statusOf: (action: ActionItem) => ActionStatus): Record<ActionStatus, number> {
  const counts: Record<ActionStatus, number> = { open: 0, doing: 0, done: 0 };
  for (const action of actions) counts[statusOf(action)] += 1;
  return counts;
}

export interface OwnerLoad {
  owner: string;
  total: number;
  open: number;
  doing: number;
  done: number;
}

/**
 * The plan split by the person who carries it: one record per owner, with the
 * same local status changes the figures use. Heaviest load first; owners with
 * the same load keep the order they first appear in the plan.
 */
export function ownerLoad(actions: ActionItem[], statusOf: (action: ActionItem) => ActionStatus): OwnerLoad[] {
  const rows = new Map<string, OwnerLoad>();
  for (const action of actions) {
    const row = rows.get(action.owner) ?? { owner: action.owner, total: 0, open: 0, doing: 0, done: 0 };
    row.total += 1;
    row[statusOf(action)] += 1;
    rows.set(action.owner, row);
  }
  return [...rows.values()]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => b.row.total - a.row.total || a.index - b.index)
    .map(({ row }) => row);
}

export const EFFORT_ORDER: ActionEffort[] = ["low", "medium", "high"];

/** How many actions ask for each level of effort. Effort never changes on this screen. */
export function countByEffort(actions: ActionItem[]): Record<ActionEffort, number> {
  const counts: Record<ActionEffort, number> = { low: 0, medium: 0, high: 0 };
  for (const action of actions) counts[action.effort] += 1;
  return counts;
}

export interface PriorityBand {
  /** Short axis label, e.g. "1" or "1-3". */
  label: string;
  /** The same band in words for a sentence, e.g. "1" or "1 sampai 3". */
  words: string;
  /** Actions whose priority falls in the band. */
  total: number;
  /** Those actions per status, after the local changes of the reader. */
  counts: Record<ActionStatus, number>;
}

/** A plan with this many distinct priorities or fewer shows each one on its own. */
const PRIORITY_LEVELS_MAX = 4;

/** Past that, priority reads as a rank, and a band is three ranks with the last one open ended. */
const RANK_BANDS: { label: string; words: string; upTo: number }[] = [
  { label: "1-3", words: "1 sampai 3", upTo: 3 },
  { label: "4-6", words: "4 sampai 6", upTo: 6 },
  { label: "7+", words: "7 ke atas", upTo: Number.POSITIVE_INFINITY },
];

/**
 * The plan cut by urgency, most urgent first (1 is most urgent), each group
 * with its actions counted per status. The API sends priority as a few levels
 * (1 to 3 today), so each level is its own group; a plan ranked one action per
 * priority is cut into bands of three instead. Every action falls in exactly
 * one group, and a group with no action is left out, so a zero always means
 * "none in this status" and never "no such group".
 */
export function priorityBands(actions: ActionItem[], statusOf: (action: ActionItem) => ActionStatus): PriorityBand[] {
  const levels = [...new Set(actions.map((action) => action.priority))].sort((a, b) => a - b);
  const cuts =
    levels.length <= PRIORITY_LEVELS_MAX
      ? levels.map((level) => ({ label: formatMetric(level, "count"), words: formatMetric(level, "count"), upTo: level }))
      : RANK_BANDS;
  const bands: PriorityBand[] = cuts.map(({ label, words }) => ({ label, words, total: 0, counts: { open: 0, doing: 0, done: 0 } }));
  for (const action of actions) {
    const band = bands[cuts.findIndex(({ upTo }) => action.priority <= upTo)] ?? bands[bands.length - 1];
    if (!band) continue;
    band.total += 1;
    band.counts[statusOf(action)] += 1;
  }
  return bands.filter((band) => band.total > 0);
}

/**
 * The keys that share the highest count, in the order given, so a sentence
 * can name one leader or say plainly that several are level.
 */
export function leadersOf<Key extends string>(order: Key[], counts: Record<Key, number>): { keys: Key[]; value: number } {
  const value = Math.max(0, ...order.map((key) => counts[key]));
  return { keys: order.filter((key) => counts[key] === value), value };
}

/** Labels joined the way a sentence lists them: "a", "a dan b", "a, b, dan c". */
export function listWords(words: string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  if (words.length === 2) return `${words[0]} dan ${words[1]}`;
  return `${words.slice(0, -1).join(", ")}, dan ${words[words.length - 1]}`;
}

export interface OwnerRows {
  /** The owners drawn one block each, heaviest load first. */
  shown: OwnerLoad[];
  /** Every owner past the cap, added up into one block; null when all fit. */
  rest: (OwnerLoad & { owners: number }) | null;
}

/**
 * The owner blocks a card draws: every owner while they fit in `max` blocks,
 * otherwise the heaviest `max - 1` and one block that adds up the rest, so
 * no action is left out of the card and its height stays near its neighbours.
 */
export function ownerRows(load: OwnerLoad[], max: number): OwnerRows {
  if (load.length <= max) return { shown: load, rest: null };
  const shown = load.slice(0, max - 1);
  const others = load.slice(max - 1);
  const rest = { owner: "", owners: others.length, total: 0, open: 0, doing: 0, done: 0 };
  for (const row of others) {
    rest.total += row.total;
    rest.open += row.open;
    rest.doing += row.doing;
    rest.done += row.done;
  }
  return { shown, rest };
}
